/**
 * PortalReceipt — Simplified receipt shown to the client after the office
 * has approved their bank transfer. Designed to look like a thermal-printer
 * receipt with the office logo, line-item breakdown, discount (if any),
 * final total, payment method, and a "PAID" stamp.
 *
 * Buttons:
 *  - 🖨️ Print  → window.print() (opens browser print dialog → Save as PDF works natively)
 *  - 💾 Download PDF → same print dialog (browser handles the PDF rendering with Arabic RTL)
 *
 * No backend PDF library is needed — the browser produces a perfect PDF.
 *
 * Style: A self-contained card with a special print stylesheet that hides
 * the rest of the app shell so the printed page contains ONLY the receipt.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Printer, Download, ArrowLeft, ArrowRight, CheckCircle2, AlertCircle, FileText,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useBranding } from "@/contexts/BrandingContext";
import { fmtDateTime, fmtNum } from "@/lib/format";
import RiyalSymbol from "@/components/RiyalSymbol";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/portal`;
const BRANDING_LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/branding/logo`;

export default function PortalReceipt() {
  const params = useParams();
  const location = useLocation();
  // The route can be one of:
  //   /portal/track/:appNum/receipt   → real receipt for a specific app
  //   /portal/preview-receipt         → STAS preview with synthetic data
  const isPreview = location.pathname.endsWith("/preview-receipt");
  const appNum = isPreview ? "preview" : params.appNum;
  const navigate = useNavigate();
  const { theme, lang, dir } = useI18n();
  const { branding } = useBranding();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const BackIcon = dir === "rtl" ? ArrowRight : ArrowLeft;

  useEffect(() => {
    // Preview mode: STAS opens this from the receipt template admin page
    // to see how a real client receipt looks with the current template.
    const url = isPreview
      ? `${API}/receipt-preview`
      : `${API}/track/${appNum}/receipt`;
    axios.get(url)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "تعذّر تحميل الإيصال"));
  }, [appNum, isPreview]);

  const css = makeStyles(theme);
  if (err) return (
    <div dir={dir} style={css.page}>
      <PrintCSS />
      <div style={css.errBox} data-testid="receipt-error">
        <AlertCircle size={16} strokeWidth={2.4} /> {err}
      </div>
      <button onClick={() => navigate(isPreview ? -1 : `/portal/track/${appNum}`)} style={css.backBtn} data-testid="receipt-back">
        <BackIcon size={14} /> {lang === "ar" ? "رجوع" : "Back"}
      </button>
    </div>
  );
  if (!data) return <div dir={dir} style={css.page}><div style={css.loading}>…</div></div>;

  const tpl = data.template || {};

  return (
    <div dir={dir} style={css.page}>
      <PrintCSS />

      {isPreview && (
        <div className="vta-receipt-toolbar" style={{
          background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.5)",
          color: "#a47e1e", padding: "8px 12px", borderRadius: 8,
          fontSize: 12, fontWeight: 800, marginBottom: 10, textAlign: "center",
        }} data-testid="receipt-preview-banner">
          {lang === "ar"
            ? "وضع المعاينة — هذه فاتورة عينة للاطلاع فقط (بيانات تجريبية)"
            : "Preview Mode — sample receipt for template verification only"}
        </div>
      )}

      <div className="vta-receipt-toolbar" style={css.toolbar}>
        <button onClick={() => navigate(isPreview ? -1 : `/portal/track/${appNum}`)} style={css.backBtn} data-testid="receipt-back">
          <BackIcon size={14} /> {lang === "ar" ? "رجوع" : "Back"}
        </button>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => printReceiptOnly(lang, dir)} style={css.actionBtn} data-testid="receipt-print">
            <Printer size={14} strokeWidth={2.4} />
            {lang === "ar" ? "طباعة" : "Print"}
          </button>
          <button onClick={() => printReceiptOnly(lang, dir)} style={{ ...css.actionBtn, background: theme.gold, color: "#0a0a0a", border: "none" }} data-testid="receipt-pdf">
            <Download size={14} strokeWidth={2.4} />
            {lang === "ar" ? "تحميل PDF" : "Download PDF"}
          </button>
        </div>
      </div>

      <article className="vta-receipt" style={css.receipt} data-testid="receipt-document">
        <header style={css.header}>
          {tpl.show_logo !== false && (
            <div style={css.logoWrap}>
              <img src={BRANDING_LOGO_URL} alt="logo" style={css.logo}
                onError={(e) => {
                  // Fallback to the default /logo.png bundled with the frontend
                  // when the dynamic branding endpoint returns 404 (no custom
                  // logo uploaded yet). Avoid an infinite-error loop by
                  // remembering that we already swapped the source.
                  if (!e.currentTarget.dataset.fallback) {
                    e.currentTarget.dataset.fallback = "1";
                    e.currentTarget.src = "/logo.png";
                  } else {
                    e.currentTarget.style.display = "none";
                  }
                }} />
            </div>
          )}
          <h1 style={css.headerTitle}>{lang === "ar" ? tpl.header_ar : tpl.header_en}</h1>
          {(tpl.subheader_ar || tpl.subheader_en) && (
            <p style={css.headerSub}>{lang === "ar" ? tpl.subheader_ar : tpl.subheader_en}</p>
          )}
          {/* STAS 2026-02-23: Official legal credentials printed INSIDE the
              receipt header — mandatory per Ministry of Tourism regulation.
              Rendered as a compact grid so it never wraps to a 2nd page. */}
          {(() => {
            const cat = lang === "ar"
              ? (branding.license_category_ar || "")
              : (branding.license_category_en || branding.license_category_ar || "");
            const lic = branding.tourism_license_number || "";
            const cr = branding.commercial_register || "";
            const labels = lang === "ar"
              ? { cat: "الفئة", lic: "تصريح السياحة", cr: "السجل التجاري" }
              : lang === "ur"
                ? { cat: "درجہ", lic: "سیاحت لائسنس", cr: "تجارتی رجسٹریشن" }
                : { cat: "Category", lic: "Tourism License", cr: "Commercial Register" };
            if (!cat && !lic && !cr) return null;
            return (
              <div style={css.legalGrid} data-testid="receipt-legal-header">
                {cat && (
                  <div style={css.legalCell}>
                    <span style={css.legalLbl}>{labels.cat}</span>
                    <span style={css.legalVal}>{cat}</span>
                  </div>
                )}
                {lic && (
                  <div style={css.legalCell}>
                    <span style={css.legalLbl}>{labels.lic}</span>
                    <span style={css.legalVal} dir="ltr">{lic}</span>
                  </div>
                )}
                {cr && (
                  <div style={css.legalCell}>
                    <span style={css.legalLbl}>{labels.cr}</span>
                    <span style={css.legalVal} dir="ltr">{cr}</span>
                  </div>
                )}
              </div>
            );
          })()}
          <div style={css.officeMetaRow}>
            {tpl.vat_number && <span>{lang === "ar" ? "الرقم الضريبي" : "VAT"}: <strong dir="ltr">{tpl.vat_number}</strong></span>}
            {tpl.phone && <span>{lang === "ar" ? "هاتف" : "Tel"}: <strong dir="ltr">{tpl.phone}</strong></span>}
          </div>
        </header>

        <div style={css.refRow}>
          <div>
            <div style={css.lbl}>{lang === "ar" ? "رقم الفاتورة" : "Invoice No."}</div>
            <div style={css.val} dir="ltr">{data.invoice_ref_number || data.application_number}</div>
          </div>
          <div>
            <div style={css.lbl}>{lang === "ar" ? "تاريخ الاعتماد" : "Approved on"}</div>
            <div style={css.val}>{fmtDateTime(data.invoice_approved_at)}</div>
          </div>
        </div>

        <div style={css.clientRow}>
          <div>
            <div style={css.lbl}>{lang === "ar" ? "العميل" : "Client"}</div>
            <div style={{ ...css.val, fontWeight: 900 }}>{data.client_name}</div>
          </div>
          <div>
            <div style={css.lbl}>{lang === "ar" ? "رقم الطلب" : "Application No."}</div>
            <div style={css.val} dir="ltr">{data.application_number}</div>
          </div>
        </div>

        <div style={css.itemsSection}>
          <div style={css.itemsHeader}>
            <span>{lang === "ar" ? "البند" : "Item"}</span>
            <span>{lang === "ar" ? "المبلغ" : "Amount"}</span>
          </div>
          {(data.items || []).map((it) => (
            <div key={it.n} data-testid={`receipt-item-${it.n}`}>
              <div style={css.itemRow}>
                <span style={css.itemLabel}>
                  <span style={css.itemNum} data-keep-color="1">{it.n}</span>
                  {it.label}
                </span>
                <span style={css.itemAmount} dir="ltr">{fmtNum(it.amount)} <RiyalSymbol size={11} /></span>
              </div>
              {it.description && (
                <div
                  data-testid={`receipt-item-desc-${it.n}`}
                  style={{
                    marginInlineStart: 28,
                    marginTop: 1, marginBottom: 3,
                    fontSize: 8.5, color: "#555",
                    fontStyle: "italic", lineHeight: 1.35,
                  }}
                >
                  {it.description}
                </div>
              )}
              {Array.isArray(it.breakdown) && it.breakdown.length > 0 && (
                <ul data-testid={`receipt-breakdown-${it.n}`}
                  style={{ margin: "2px 0 6px 28px", paddingInlineStart: 14,
                    listStyle: "disc", fontSize: 10.5, color: "#555", lineHeight: 1.7 }}>
                  {it.breakdown.map((b, bi) => <li key={bi}>{b}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>

        {Number(data.discount_amount || 0) > 0 && (
          <>
            <div style={css.summaryRow}>
              <span>{lang === "ar" ? "الإجمالي قبل الخصم" : "Subtotal"}</span>
              <span dir="ltr">{fmtNum(data.subtotal_amount)} <RiyalSymbol size={11} /></span>
            </div>
            <div style={{ ...css.summaryRow, color: "#dc2626", fontWeight: 800 }} data-testid="receipt-discount">
              <span>{lang === "ar" ? `خصم ${fmtNum(data.discount_percent)}%` : `Discount ${fmtNum(data.discount_percent)}%`}</span>
              <span dir="ltr">− {fmtNum(data.discount_amount)} <RiyalSymbol size={11} color="#dc2626" /></span>
            </div>
          </>
        )}

        <div style={css.totalRow} data-keep-color="1" data-testid="receipt-total">
          <span>
            {data.paid
              ? (lang === "ar" ? "الإجمالي المدفوع" : "Total paid")
              : (lang === "ar" ? "الإجمالي المستحق" : "Total due")}
          </span>
          <span dir="ltr">{fmtNum(data.final_total)} <RiyalSymbol size={16} /></span>
        </div>

        {/* Payment block — only when there is at least one payment */}
        {data.payment && (
          <div style={css.paymentInfo}>
            <CheckCircle2 size={14} strokeWidth={2.4} style={{ color: data.paid ? "#16a34a" : "#f59e0b" }} />
            <span>{lang === "ar" ? "طريقة الدفع: تحويل بنكي" : "Payment: bank transfer"}</span>
            {data.payment?.bank_reference && (
              <span dir="ltr" style={{ color: theme.textMuted }}>
                · {lang === "ar" ? "مرجع البنك" : "Bank ref"}: <strong>{data.payment.bank_reference}</strong>
              </span>
            )}
          </div>
        )}

        {(() => {
          // Dynamic stamp based on payment_state.
          const state = data.payment_state || (data.paid ? "approved" : "none");
          let label, color;
          if (state === "approved") {
            label = lang === "ar" ? (tpl.stamp_text_ar || "مدفوع") : (tpl.stamp_text_en || "PAID");
            color = "#16a34a";
          } else if (state === "pending") {
            label = lang === "ar" ? "بانتظار اعتماد السداد" : "PAYMENT UNDER REVIEW";
            color = "#0ea5e9";
          } else if (state === "rejected") {
            label = lang === "ar" ? "السداد مرفوض" : "PAYMENT REJECTED";
            color = "#dc2626";
          } else {
            label = lang === "ar" ? "بانتظار السداد" : "AWAITING PAYMENT";
            color = "#f59e0b";
          }
          return (
            <div style={{
              ...css.stamp,
              border: `3px double ${color}`,
              color,
              fontSize: state === "approved" ? 18 : 12,
              padding: state === "approved" ? "6px 12px" : "6px 10px",
              width: state === "approved" ? 110 : "auto",
              maxWidth: 240,
            }} data-testid="receipt-stamp">
              {label}
            </div>
          );
        })()}

        <footer style={css.footer}>
          {data.disclaimer && (
            <div style={{
              background: "#f9fafb", border: "1px dashed #aaa", color: "#444",
              padding: "5px 7px", borderRadius: 5, fontSize: 7.5,
              lineHeight: 1.35, marginBottom: 4, textAlign: "start",
            }} data-testid="receipt-disclaimer">
              <strong style={{ fontSize: 8.5 }}>
                {lang === "ar"
                  ? (data.disclaimer.title_ar || "سياسة الخدمة والفواتير")
                  : (data.disclaimer.title_en || "Service & Invoice Policy")}
              </strong>
              <ol style={{ margin: "2px 0 0", paddingInlineStart: 12 }}>
                {((lang === "ar" ? data.disclaimer.points_ar : data.disclaimer.points_en) || []).map((p, i) => (
                  <li key={i} style={{ marginBottom: 0 }}>{p}</li>
                ))}
                {/* STAS 2026-02-23: 10% refund clause appended into the SAME
                    existing policy box (not a new one). Injected client-side
                    so backend disclaimer template stays untouched. */}
                <li key="refund-10-percent" style={{ marginBottom: 0, fontWeight: 700 }} data-testid="disclaimer-refund-10">
                  {lang === "ar"
                    ? "سياسة الاسترداد: يحتفظ المكتب بنسبة 10% كأتعاب أولية ويُردّ للعميل صافي 90%. للإدارة صلاحية الإعفاء الكلي (100%) في حالات استثنائية. الاسترداد يتطلب موافقة الإدارة."
                    : lang === "ur"
                      ? "ریفنڈ پالیسی: دفتر 10% ابتدائی فیس رکھتا ہے (کلائنٹ کو 90% واپس)؛ انتظامیہ استثنائی طور پر مکمل معافی (100%) دے سکتی ہے۔ ریفنڈ کے لیے انتظامیہ کی منظوری لازمی ہے۔"
                      : "Refund policy: the office retains 10% as initial fees; the client receives a net 90%. Senior management may grant a full waiver (100%) in exceptional cases. Refunds require management approval."}
                </li>
              </ol>
            </div>
          )}
          {data.qr_data_url && (
            <div style={{
              marginTop: 4, marginBottom: 4, display: "flex",
              alignItems: "center", gap: 8, justifyContent: "center",
              borderTop: "1px dashed #aaa", paddingTop: 4,
            }} data-testid="receipt-qr">
              <img src={data.qr_data_url} alt="QR" style={{ width: 48, height: 48, background: "#fff" }} />
              <div style={{ textAlign: "start", fontSize: 8, color: "#333", lineHeight: 1.3 }}>
                <div style={{ fontWeight: 800, color: "#000" }}>
                  {lang === "ar" ? "التحقق من أصالة الفاتورة" : "Verify this invoice"}
                </div>
                <div style={{ marginTop: 1, fontFamily: "monospace", fontSize: 7.5, wordBreak: "break-all" }} dir="ltr">
                  {data.invoice_ref_number}
                </div>
              </div>
            </div>
          )}
          {(tpl.footer_ar || tpl.footer_en) && (
            <p style={css.footerText}>{lang === "ar" ? tpl.footer_ar : tpl.footer_en}</p>
          )}
          {(tpl.legal_note_ar || tpl.legal_note_en) && (
            <p style={css.legalText}>
              <FileText size={11} strokeWidth={2.2} style={{ verticalAlign: "-2px", marginInlineEnd: 4 }} />
              {lang === "ar" ? tpl.legal_note_ar : tpl.legal_note_en}
            </p>
          )}
          {tpl.website && (
            <p style={css.website} dir="ltr">{tpl.website}</p>
          )}
          {/* Service & Invoice Policy already appears via the existing
              PolicyCard flow (`PortalTrack` before receipt upload + printed
              receipt template). No extra PolicyBlock here — avoids duplication. */}
        </footer>
      </article>
    </div>
  );
}

function PrintCSS() {
  return (
    <style>{`
      @media print {
        @page {
          size: A5 portrait;
          margin: 0;
        }
      }
    `}</style>
  );
}

/**
 * Reliable "print only the invoice" — opens a fresh, isolated window that
 * contains ONLY the receipt HTML + minimal print CSS + a copy of the current
 * document's <style> and <link rel="stylesheet"> tags so fonts and colours
 * survive. This bypasses every parent-layout / visibility trick and always
 * yields a clean single-page PDF or print job on Chrome, Safari and Edge.
 */
function printReceiptOnly(lang, dir) {
  const src = document.querySelector(".vta-receipt");
  if (!src) { window.print(); return; }
  const styleTags = Array.from(document.querySelectorAll("style"))
    .map((s) => s.outerHTML).join("");
  const linkTags = Array.from(document.querySelectorAll("link[rel='stylesheet']"))
    .map((l) => l.outerHTML).join("");
  const w = window.open("", "_blank", "width=800,height=1000,noopener=false");
  if (!w) { window.print(); return; }
  w.document.open();
  w.document.write(`<!doctype html>
    <html lang="${lang}" dir="${dir}">
      <head>
        <meta charset="utf-8" />
        <title></title>
        ${linkTags}
        ${styleTags}
        <style>
          @page { size: A5 portrait; margin: 0; }
          @media print {
            html, body {
              margin: 0 !important; padding: 0 !important;
              background: #fff !important;
              width: 148mm; height: 210mm;
            }
            .vta-receipt-toolbar { display: none !important; }
            .vta-receipt {
              box-sizing: border-box;
              width: 148mm !important;
              max-width: 148mm !important;
              max-height: 210mm !important;
              padding: 3mm 5mm !important;
              margin: 0 !important;
              border-radius: 0 !important;
              box-shadow: none !important;
              overflow: hidden !important;
              /* No global zoom. Content is compressed at the source (logo
                 wrapper cropped tight, disclaimer 7pt, compact QR row) so
                 the receipt fills A5 naturally without shrinking. */
              transform-origin: top center;
              break-inside: avoid;
              page-break-inside: avoid;
              page-break-after: avoid;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .vta-receipt * {
              page-break-inside: avoid;
              break-inside: avoid;
            }
          }
          html, body {
            margin: 0; padding: 0; background: #fff; color: #000;
            font-family: 'Tajawal','Segoe UI', -apple-system, sans-serif;
          }
          .vta-receipt { position: relative; display: block; width: 100%; }
          .vta-receipt-toolbar { display: none; }
        </style>
      </head>
      <body>${src.outerHTML}</body>
    </html>`);
  w.document.close();
  // Wait for fonts + images to hydrate, then print. Close ONLY on afterprint.
  const doPrint = async () => {
    try {
      if (w.document.fonts && w.document.fonts.ready) {
        try { await w.document.fonts.ready; } catch { /* ignore */ }
      }
      const imgs = Array.from(w.document.images || []);
      await Promise.all(imgs.map((img) =>
        img.complete ? Promise.resolve()
          : (img.decode ? img.decode().catch(() => {}) : Promise.resolve())
      ));
      w.addEventListener("afterprint", () => {
        try { w.close(); } catch { /* ignore */ }
      }, { once: true });
      w.focus();
      w.print();
    } catch {
      try { w.print(); } catch { /* ignore */ }
    }
  };
  if (w.document.readyState === "complete") {
    doPrint();
  } else {
    w.addEventListener("load", doPrint, { once: true });
  }
}

function makeStyles(theme) {
  return {
    page: { padding: 18, background: theme.bg, minHeight: "100vh", color: theme.text },
    toolbar: { display: "flex", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 },
    backBtn: {
      background: "transparent", border: `1px solid ${theme.border}`, color: theme.text,
      padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
    },
    actionBtn: {
      background: "transparent", border: `1px solid ${theme.border}`, color: theme.text,
      padding: "8px 14px", borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 6,
    },
    receipt: {
      maxWidth: 520, margin: "0 auto", background: "#fff", color: "#000",
      borderRadius: 16, padding: "12px 20px", boxShadow: "0 30px 60px rgba(0,0,0,0.4)",
      fontFamily: "'Tajawal', 'Cairo', sans-serif", lineHeight: 1.4,
    },
    header: { textAlign: "center", borderBottom: "1.5px dashed #aaa", paddingBottom: 6, marginBottom: 6 },
    logoWrap: {
      width: 220, height: 120,
      marginLeft: "auto", marginRight: "auto",
      marginTop: 0, marginBottom: 4,
      display: "flex", alignItems: "center", justifyContent: "center",
      overflow: "hidden",
    },
    logo: {
      width: 220, height: 220,
      display: "block",
      objectFit: "contain",
      // Crop the transparent margin baked into the source PNG so the
      // emblem visually fills the wrapper without wasting layout height.
      transform: "scale(1.7)",
      transformOrigin: "center center",
    },
    headerTitle: { margin: 0, fontSize: 16, fontWeight: 900, color: "#000" },
    headerSub: { margin: "3px 0 6px", fontSize: 11, color: "#555" },
    legalGrid: {
      display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 4,
      margin: "4px 0 4px", padding: "4px 6px",
      border: "1px solid #333", borderRadius: 5, background: "#fafafa",
    },
    legalCell: {
      display: "flex", flexDirection: "column", alignItems: "center",
      textAlign: "center", gap: 1,
    },
    legalLbl: { fontSize: 8, color: "#666", fontWeight: 600, lineHeight: 1.1 },
    legalVal: { fontSize: 10, color: "#000", fontWeight: 800, lineHeight: 1.1 },
    officeMetaRow: {
      display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 8,
      fontSize: 9.5, color: "#555", marginTop: 3,
    },
    refRow: { display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6, fontSize: 11 },
    clientRow: { display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6, fontSize: 11 },
    lbl: { fontSize: 9, color: "#555", marginBottom: 1 },
    val: { fontWeight: 700, color: "#000", fontSize: 12 },
    itemsSection: { borderTop: "1px dashed #aaa", borderBottom: "1px dashed #aaa", padding: "4px 0", marginBottom: 4 },
    itemsHeader: {
      display: "flex", justifyContent: "space-between", fontSize: 10,
      fontWeight: 800, color: "#555", padding: "2px 0", borderBottom: "1px solid #ddd", marginBottom: 2,
    },
    itemRow: {
      display: "flex", justifyContent: "space-between", padding: "3px 0",
      fontSize: 12, borderBottom: "1px dotted #eee",
    },
    itemLabel: { display: "inline-flex", alignItems: "center", gap: 6, color: "#000" },
    itemNum: {
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 18, height: 18, borderRadius: "50%", background: "#000", color: "#fff",
      fontSize: 10, fontWeight: 900, fontFamily: "monospace",
    },
    itemAmount: { fontWeight: 800, fontFamily: "monospace", color: "#000" },
    summaryRow: {
      display: "flex", justifyContent: "space-between", padding: "3px 4px",
      fontSize: 12, fontFamily: "monospace", color: "#000",
    },
    totalRow: {
      display: "flex", justifyContent: "space-between", padding: "8px 14px",
      background: "#000", color: "#fff", borderRadius: 10, fontSize: 14, fontWeight: 900,
      marginTop: 6, marginBottom: 6, fontFamily: "monospace",
    },
    paymentInfo: {
      display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6,
      fontSize: 10.5, color: "#000", padding: "4px 0", borderBottom: "1px dashed #aaa",
      marginBottom: 6,
    },
    stamp: {
      width: 110, margin: "2px auto 6px", textAlign: "center",
      padding: "5px 10px", border: "3px double #16a34a", color: "#16a34a",
      borderRadius: 8, fontWeight: 900, fontSize: 18, letterSpacing: 2,
      transform: "rotate(-6deg)",
    },
    footer: { borderTop: "1.5px dashed #aaa", paddingTop: 4, textAlign: "center" },
    footerText: { margin: "0 0 3px", fontSize: 10.5, color: "#000" },
    legalText: { margin: "2px 0", fontSize: 9, color: "#555", lineHeight: 1.3 },
    website: { margin: "4px 0 0", fontSize: 9.5, color: "#555", fontFamily: "monospace" },
    loading: { textAlign: "center", padding: 40, color: theme.textMuted },
    errBox: {
      background: "rgba(220,38,38,0.12)", color: "#dc2626", padding: "12px 14px",
      borderRadius: 10, marginBottom: 16, display: "inline-flex", gap: 8, alignItems: "center",
      fontWeight: 700, fontSize: 13,
    },
  };
}
